import {
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  DefaultColorStyle,
  StyleProp,
  type TLDefaultColorStyle,
  type TLBaseBinding,
  type TLBaseShape,
} from "@tldraw/tlschema";
/**
 * The callout shape: an architectural leader note. Lettering sits at `head`,
 * a leader runs to `target`, and the terminator says what the leader lands
 * on (arrow = an edge, dot = a surface or area). A tag draws the head as a
 * keynote bubble instead of plain lettering, and then the text is the key
 * the legend explains. `target` is either a `callout` binding to a shape (so
 * the leader follows the element it annotates) or the free point in props.
 * Points are absolute page-space (Y-down); the shape's own x/y stay 0, the
 * same arrangement the dim uses.
 */
import { T } from "@tldraw/validate";

/** How the head is drawn: plain lettering, or a keynote bubble holding a
 *  key ("A3", "05 12 00") that a legend explains. */
export const CalloutTagStyle = StyleProp.defineEnum("sketch:calloutTag", {
  defaultValue: "none",
  values: ["none", "circle", "hex"],
});
/** What the leader lands on: arrow = an edge, dot = a surface or area,
 *  none = the leader just reaches (a keynote on its own tag). */
export const CalloutTerminatorStyle = StyleProp.defineEnum(
  "sketch:calloutTerminator",
  { defaultValue: "arrow", values: ["arrow", "dot", "none"] },
);
export type CalloutTag = T.TypeOf<typeof CalloutTagStyle>;
export type CalloutTerminator = T.TypeOf<typeof CalloutTerminatorStyle>;

/** A note is a block of lettering, not a line that runs on: it wraps at a
 *  width the drafter can drag. Screen pixels, like every annotation metric,
 *  so a note wraps at the same place at every zoom. About six words. */
export const DEFAULT_CALLOUT_WIDTH = 200;

/** The longest key a tag holds ("05 12 00" is a spec section). */
export const KEY_MAX = 8;

/** A tag is a keynote bubble: it holds a key a legend explains, never a
 *  note. Every write path (agent actions, the editor, migration) holds the
 *  line here, so a paragraph in a bubble is not a state a sketch can be in. */
export function isKey(text: string): boolean {
  return text.trim().length <= KEY_MAX;
}

export interface CalloutProps {
  /** The note, or the key when the head is a tag. */
  text: string;
  /** The ink: black on a sheet, red when it is review markup. */
  color: TLDefaultColorStyle;
  /** Where the lettering wraps, in screen pixels. */
  width: number;
  tag: CalloutTag;
  terminator: CalloutTerminator;
  /** Where the lettering (or tag) sits, page units. Null asks the sheet to
   *  place it: the note stacks in a notes column beside the drawing
   *  (`callout-stack.ts`), the way an unplaced dim stacks outside it. */
  head: { x: number; y: number } | null;
  /** Where the leader points, page units. */
  target: { x: number; y: number };
}

declare module "@tldraw/tlschema" {
  interface TLGlobalShapePropsMap {
    callout: CalloutProps;
  }
}

export type CalloutShape = TLBaseShape<"callout", CalloutProps>;

const point = T.object({ x: T.number, y: T.number });

export const calloutProps: Record<keyof CalloutProps, T.Validatable<any>> = {
  text: T.string,
  color: DefaultColorStyle,
  width: T.positiveNumber,
  tag: CalloutTagStyle,
  terminator: CalloutTerminatorStyle,
  head: point.nullable(),
  target: point,
};

const calloutVersions = createShapePropsMigrationIds("callout", {
  AddWidth: 1,
  SheetPlacesHead: 2,
  TagHoldsKey: 3,
  AddColor: 4,
});

export const calloutMigrations = createShapePropsMigrationSequence({
  sequence: [
    {
      // Notes used to run on in one line for as long as you typed.
      id: calloutVersions.AddWidth,
      up(props) {
        props.width = DEFAULT_CALLOUT_WIDTH;
      },
      down(props) {
        delete props.width;
      },
    },
    {
      // Every head used to be authored; a null head is now the sheet's to
      // place. Existing notes keep the spot they were given.
      id: calloutVersions.SheetPlacesHead,
      up() {},
      down(props) {
        if (props.head === null) props.head = { ...props.target };
      },
    },
    {
      // A note that had been drawn in a bubble it could not fit is lettering.
      id: calloutVersions.TagHoldsKey,
      up(props) {
        if (!isKey(props.text)) props.tag = "none";
      },
      down() {},
    },
    {
      // Callouts were all lettered in the drawing ink.
      id: calloutVersions.AddColor,
      up(props) {
        props.color = "black";
      },
      down(props) {
        delete props.color;
      },
    },
  ],
});

export interface CalloutBindingProps {
  /** Normalized anchor on the target shape's bounds. */
  anchor: { x: number; y: number };
  /** For polyline targets: the point id the leader lands on, so the binding
   *  survives edits to unrelated vertices (bounds anchors do not). */
  vertex?: string;
}

declare module "@tldraw/tlschema" {
  interface TLGlobalBindingPropsMap {
    callout: CalloutBindingProps;
  }
}

export type CalloutBinding = TLBaseBinding<"callout", CalloutBindingProps>;

export const calloutBindingProps: Record<
  keyof CalloutBindingProps,
  T.Validatable<any>
> = {
  anchor: point,
  vertex: T.string.optional(),
};

/**
 * A callout has one leader, so it has one binding, and its id is derived:
 * every write path (agent actions, the UI tool) mints the same id, so a
 * re-bind replaces rather than accumulates.
 */
export function calloutBindingId(calloutId: string): string {
  return `binding:${calloutId.replace(/^shape:/, "")}-leader`;
}
